import React, { useState } from 'react';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './AjoutArticle.css';

function AjoutArticle() {
  const navigate = useNavigate();
  const [titre, setTitre] = useState('');
  const [resume, setResume] = useState('');
  const [contenuComplet, setContenuComplet] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [positionImage, setPositionImage] = useState<'gauche' | 'droite' | 'aucune'>('gauche');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Vérification du type de fichier
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image');
        return;
      }
      
      setImageFile(file);
      
      // Création de la prévisualisation
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titre || !resume || !contenuComplet) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let imageUrl = '';
      let finalPositionImage = positionImage;
      
      // Si une image est sélectionnée
      if (imageFile) {
        const storage = getStorage();
        const timestamp = Date.now();
        const imageRef = ref(storage, `articles/${timestamp}_${imageFile.name}`);
        
        const snapshot = await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      } else {
        finalPositionImage = 'aucune';
      }

      // Préservation des espaces et des retours à la ligne
      const preserveFormatting = (text: string) => {
        return text
          .replace(/\n/g, '<br>')  // Remplace les retours à la ligne par des <br>
          .replace(/ {2,}/g, ' &nbsp;'); // Préserve les espaces multiples
      };

      const article = {
        titre: titre.trim(),
        resume: preserveFormatting(resume),
        contenuComplet: preserveFormatting(contenuComplet),
        date: new Date().toLocaleDateString('fr-FR'),
        image: imageUrl,
        positionImage: finalPositionImage,
        dateCreation: new Date()
      };

      const db = getFirestore();
      await addDoc(collection(db, 'Articles'), article);
      navigate('/mapage/articles');

    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de la création de l\'article');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ajout-article-container">
      <h2>Ajouter un article</h2>
      <form onSubmit={handleSubmit} className="article-form">
        <div className="form-group">
          <label htmlFor="titre">Titre</label>
          <input
            type="text"
            id="titre"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="resume">Résumé (visible sur la page actualités)</label>
          <textarea
            id="resume"
            value={resume}
            onChange={(e) => setResume(e.target.value)}
            required
            rows={4}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contenuComplet">Contenu complet</label>
          <textarea
            id="contenuComplet"
            value={contenuComplet}
            onChange={(e) => setContenuComplet(e.target.value)}
            required
            rows={8}
          />
        </div>

        <div className="form-group">
          <label htmlFor="image">Image</label>
          <input
            type="file"
            id="image"
            name="image"
            accept="image/*"
            onChange={handleImageChange}
          />
          {imagePreview && (
            <div className="image-preview">
              <img src={imagePreview} alt="Prévisualisation" />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="positionImage">Position de l'image</label>
          <select
            id="positionImage"
            value={positionImage}
            onChange={(e) => setPositionImage(e.target.value as 'gauche' | 'droite' | 'aucune')}
          >
            <option value="gauche">À gauche</option>
            <option value="droite">À droite</option>
            <option value="aucune">Aucune image</option>
          </select>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/mapage/articles')}
            className="cancel-btn"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="submit-btn"
          >
            {loading ? 'Création en cours...' : 'Créer l\'article'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AjoutArticle;